/**
 * reasonTags — plain-English copy for the reason tags the negative-keyword scorer attaches to a
 * candidate (backend/app/recommendations/negative_keywords/algorithm_v3.py, `matched_rules[0].tags`).
 * The Approvals page showed them raw (`pen_out_of_geo_location`); AdOps read them dozens of times a day.
 *
 * `TAG_LABELS` covers every tag the scorer emits; `tagLabel` falls back to a humanised form so a tag
 * added later still reads, and `primaryTag` picks the one most worth a glance in the row.
 */

export const TAG_LABELS: Record<string, string> = {
  pen_out_of_geo_location: 'Outside your target countries',
  pen_informational_query: 'Research query',
  route_to_brand_campaign: 'Your own brand',
  pen_competitor_wrong_campaign: 'Competitor search',
  pen_unrelated_competitor: 'Unrelated business',
  pen_other_brand: 'Another brand',
  pen_other_brand_softened: 'Possibly another brand',
  pen_non_brand_term: 'Not your brand',
  pen_non_competitor_term: 'Not a competitor',
  pen_low_relevance: 'Low relevance',
  poor_business_relevance: 'Low relevance',
  poor_campaign_alignment: 'Wrong campaign',
  poor_intent: 'Weak intent',
  poor_performance: 'Poor performance',
  penalty_cap_applied: 'Several penalties',
  v3_brand_rescue: 'Brand protection',
  v3_transfer: 'Move to another campaign',
  phrase_only: 'Phrase match only',
  skip_brand_location: 'Kept: brand and location',
  skip_own_brand: 'Kept: your own brand',
  skip_brand_adjacent_competitor: 'Kept: brand-adjacent',
  skip_competitor_prospecting_overlap: 'Kept: overlaps your terms',
  skip_category_match_llm_not_competitor: 'Kept: matches your category',
  boost_location: 'Boosted: location search',
  boost_related_company: 'Boosted: related company',
  correct_campaign_competitor: 'Right campaign',
  v3_error: 'Analysis error',
  v3_fallback: 'Fallback decision',
};

/** Most specific reason first; only these become the row pill. */
export const PRIMARY_TAG_ORDER: readonly string[] = [
  'pen_out_of_geo_location',
  'pen_informational_query',
  'route_to_brand_campaign',
  'pen_competitor_wrong_campaign',
  'pen_unrelated_competitor',
  'pen_other_brand',
  'pen_other_brand_softened',
  'pen_non_competitor_term',
  'pen_non_brand_term',
  'pen_low_relevance',
  'v3_brand_rescue',
  'v3_transfer',
  'poor_intent',
  'poor_performance',
];

const PREFIXES = ['pen_', 'skip_', 'boost_', 'v3_'];

function humaniseTag(tag: string): string {
  let rest = tag;
  for (const prefix of PREFIXES) {
    if (rest.startsWith(prefix)) {
      rest = rest.slice(prefix.length);
      break;
    }
  }
  const words = rest.split('_').filter(Boolean).join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : tag;
}

export function tagLabel(tag: string): string {
  return TAG_LABELS[tag] ?? humaniseTag(tag);
}

export function primaryTag(tags: readonly string[] | undefined | null): string | null {
  if (!tags || tags.length === 0) return null;
  return PRIMARY_TAG_ORDER.find((candidate) => tags.includes(candidate)) ?? null;
}

export function isPenaltyTag(tag: string): boolean {
  return tag.startsWith('pen');
}
